import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Toaster } from 'react-hot-toast';
import { ClerkProvider } from '@clerk/clerk-react';
import { frFR } from '@clerk/localizations';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Layout from './components/layout/Layout';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import RegisterPage from './pages/RegisterPage';
import OnboardingPage from './pages/OnboardingPage';
import DashboardRouter from './pages/DashboardRouter';
import StructurePage from './pages/StructurePage';
import GererSalariesPage from './pages/GererSalariesPage';
import EmployeeDetailPage from './pages/EmployeeDetailPage';
import ObligationsPage from './pages/ObligationsPage';
import DocumentsPage from './pages/DocumentsPage';
import AuditPage from './pages/AuditPage';
import SurveyPage from './pages/SurveyPage';
import SurveyResponsePage from './pages/SurveyResponsePage';
import MarketplacePage from './pages/MarketplacePage';
import GuidesPage from './pages/GuidesPage';
import UserSettingsPage from './pages/UserSettingsPage';
import MesPaiePage from './pages/MesPaiePage';
import PayslipsRhPage from './pages/PayslipsRhPage';
import AbsencesRhPage from './pages/AbsencesRhPage';
import ExpenseReportsRhPage from './pages/ExpenseReportsRhPage';
import OrganigrammePage from './pages/OrganigrammePage';
import AnnuairePage from './pages/AnnuairePage';
import TimesheetsRhPage from './pages/TimesheetsRhPage';
import MesAbsencesPage from './pages/MesAbsencesPage';
import MesTempsPage from './pages/MesTempsPage';
import NotesDeFraisPage from './pages/NotesDeFraisPage';
import CreateOrganizationPage from './pages/CreateOrganizationPage';
import LoadingSpinner from './components/ui/LoadingSpinner';
import { isCollaborateurUser, shouldSkipOnboarding } from './utils/roles';

import './styles/global.css';

const CLERK_PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || '';

const ProtectedRoute: React.FC<{ children: React.ReactNode; requireOnboarding?: boolean }> = ({
  children,
  requireOnboarding = false,
}) => {
  const { isAuthenticated, isLoading, user, needsOrganization } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <LoadingSpinner size={40} />
      </div>
    );
  }

  if (needsOrganization) {
    return <Navigate to="/creer-organisation" replace />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireOnboarding && user && !user.onboarding_completed && !shouldSkipOnboarding(user)) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

const OnboardingRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user, needsOrganization } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <LoadingSpinner size={40} />
      </div>
    );
  }

  if (needsOrganization) {
    return <Navigate to="/creer-organisation" replace />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.onboarding_completed || shouldSkipOnboarding(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const RhOnlyGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <LoadingSpinner size={40} />
      </div>
    );
  }
  if (isCollaborateurUser(user)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

const CollaborateurOnlyGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <LoadingSpinner size={40} />
      </div>
    );
  }
  if (!isCollaborateurUser(user)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};


const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user, needsOrganization } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <LoadingSpinner size={40} />
      </div>
    );
  }

  if (needsOrganization) {
    return <Navigate to="/creer-organisation" replace />;
  }

  if (isAuthenticated) {
    const dest = user?.onboarding_completed || shouldSkipOnboarding(user) ? '/dashboard' : '/onboarding';
    return <Navigate to={dest} replace />;
  }

  return <>{children}</>;
};

const LandingRoute: React.FC = () => {
  const { isAuthenticated, isLoading, user, needsOrganization } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <LoadingSpinner size={40} />
      </div>
    );
  }

  if (needsOrganization) {
    return <Navigate to="/creer-organisation" replace />;
  }

  if (isAuthenticated) {
    const dest = user?.onboarding_completed || shouldSkipOnboarding(user) ? '/dashboard' : '/onboarding';
    return <Navigate to={dest} replace />;
  }

  return <LandingPage />;
};

const CreateOrgRoute: React.FC = () => {
  const { isLoading, isAuthenticated, needsOrganization } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <LoadingSpinner size={40} />
      </div>
    );
  }

  if (isAuthenticated && !needsOrganization) {
    return <Navigate to="/dashboard" replace />;
  }

  return <CreateOrganizationPage />;
};

const AppRoutes: React.FC = () => {
  return (
    <AnimatePresence mode="wait">
      <Routes>
        <Route path="/" element={<LandingRoute />} />
        <Route path="/creer-organisation" element={<CreateOrgRoute />} />
        {/* Public routes */}
        <Route
          path="/login/*"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register/*"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        <Route path="/survey/:id/respond" element={<SurveyResponsePage />} />
        <Route path="/onboarding" element={<OnboardingRoute><OnboardingPage /></OnboardingRoute>} />

        {/* Protected routes */}
        <Route
          element={
            <ProtectedRoute requireOnboarding>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardRouter />} />
          <Route path="/ma-paie" element={<CollaborateurOnlyGuard><MesPaiePage /></CollaborateurOnlyGuard>} />
          <Route path="/absences" element={<CollaborateurOnlyGuard><MesAbsencesPage /></CollaborateurOnlyGuard>} />
          <Route path="/mes-temps" element={<CollaborateurOnlyGuard><MesTempsPage /></CollaborateurOnlyGuard>} />
          <Route path="/notes-de-frais" element={<CollaborateurOnlyGuard><NotesDeFraisPage /></CollaborateurOnlyGuard>} />
          <Route path="/organigramme" element={<OrganigrammePage />} />
          <Route path="/annuaire" element={<CollaborateurOnlyGuard><AnnuairePage /></CollaborateurOnlyGuard>} />
          <Route path="/structure" element={<RhOnlyGuard><StructurePage /></RhOnlyGuard>} />
          <Route path="/employes" element={<RhOnlyGuard><GererSalariesPage /></RhOnlyGuard>} />
          <Route path="/employes/:id" element={<RhOnlyGuard><EmployeeDetailPage /></RhOnlyGuard>} />
          <Route path="/obligations" element={<RhOnlyGuard><ObligationsPage /></RhOnlyGuard>} />
          <Route path="/documents" element={<RhOnlyGuard><DocumentsPage /></RhOnlyGuard>} />
          <Route path="/bulletins-paie" element={<RhOnlyGuard><PayslipsRhPage /></RhOnlyGuard>} />
          <Route path="/absences-rh" element={<RhOnlyGuard><AbsencesRhPage /></RhOnlyGuard>} />
          <Route path="/notes-de-frais-rh" element={<RhOnlyGuard><ExpenseReportsRhPage /></RhOnlyGuard>} />
          <Route path="/feuilles-temps-rh" element={<RhOnlyGuard><TimesheetsRhPage /></RhOnlyGuard>} />
          <Route path="/audit" element={<RhOnlyGuard><AuditPage /></RhOnlyGuard>} />
          <Route path="/surveys" element={<RhOnlyGuard><SurveyPage /></RhOnlyGuard>} />
          <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
          <Route path="/marketplace" element={<RhOnlyGuard><MarketplacePage /></RhOnlyGuard>} />
          <Route path="/guides" element={<GuidesPage />} />
          <Route path="/parametres" element={<UserSettingsPage />} />
          {/* Redirection anciens liens */}
          <Route path="/employees" element={<Navigate to="/employes" replace />} />
          <Route path="/employees/:id" element={<Navigate to="/employes" replace />} />
        </Route>

        {/* Default redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  );
};

const App: React.FC = () => {
  if (!CLERK_PUBLISHABLE_KEY) {
    return (
      <div style={{ padding: '2rem', fontFamily: 'system-ui' }}>
        Configuration Clerk manquante : définissez REACT_APP_CLERK_PUBLISHABLE_KEY.
      </div>
    );
  }

  return (
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      localization={frFR}
      signInUrl="/login"
      signUpUrl="/register"
      signInFallbackRedirectUrl="/dashboard"
      signUpFallbackRedirectUrl="/creer-organisation"
    >
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
            <AppRoutes />
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  borderRadius: '8px',
                  background: '#fff',
                  color: '#2C3E50',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  fontSize: '0.875rem',
                  fontFamily: "'Inter', sans-serif",
                },
              }}
            />
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    </ClerkProvider>
  );
};

export default App;
